import React, { useEffect, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../../../util/axiosInstance";
import { Apiurl } from "../../../util/apiurl";
import Loader from '../../layout/lodaer';

function RegulatoryUpdates() {
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };
  };

  const goToView = (id) => {
    navigate(`/RegulatoryUpdatesView/${id}`);
  };

  const IconCellRenderer = (params) => (
    <div className="table-actions">
      <button type="button" className="btn btn-default" onClick={() => goToView(params.data.id)}>
        <i className="fa fa-eye"></i>
      </button>
    </div>
  );

  const [columnDefs] = useState([
    { headerName: "S No.", field: "srNo", cellClass: 'sno-column', width: 100 },
    { headerName: "Date of Amendment", field: "dateOfAmendment", cellClass: 'date-column' },
    { headerName: "Ministry/Department", field: "department", cellClass: 'department-column' },
    { headerName: "Notification", field: "regulatoryName", cellClass: 'notification-column' },
    {
      headerName: "Know More",
      field: "status",
      flex: 1.5,
      cellClass: 'know-more-column',
      cellRenderer: (params) => <IconCellRenderer {...params} />,
    },
  ]);

  const [defaultColDef] = useState({
    sortable: true,
    filter: true,
  });

  const fetchData = async (year = "", department = "", searchTerm = "") => {
    setError(null);
    try {
      let results = [];

      if (year) {
        const response = await axiosInstance.get(`${Apiurl.searchByYear}?year=${year}`);
        results = response.data;
      } else if (department) {
        const response = await axiosInstance.get(`${Apiurl.searchByDepartment}?department=${department.toLowerCase()}`);
        results = response.data;
      } else if (searchTerm) {
        const encodedSearchTerm = encodeURIComponent(searchTerm);

        const [responseDepartment, responseRegulatoryName] = await Promise.allSettled([
          axiosInstance.get(`${Apiurl.search}?department=${encodedSearchTerm}`),
          axiosInstance.get(`${Apiurl.search}?regulatoryName=${encodedSearchTerm}`),
        ]);

        if (responseDepartment.status === "fulfilled" && responseDepartment.value.status === 200) {
          results = responseDepartment.value.data;
        } else if (responseRegulatoryName.status === "fulfilled" && responseRegulatoryName.value.status === 200) {
          results = responseRegulatoryName.value.data;
        } else {
          throw new Error("No results found or API call failed.");
        }
      } else {
        const response = await axiosInstance.get(`${Apiurl.viewAll}`);
        results = response.data;
      }

      const filteredData = results
        .filter(item => /^[A-Z][a-z]{2}\s\d{2}\s\d{4}$/.test(item.dateOfAmendment))
        .map((item, index) => ({
          ...item,
          srNo: index + 1
        }));

      setRowData(filteredData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('No Record Found.');
      setRowData([]);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), []);

  useEffect(() => {
    if (initialLoad) {
      setTimeout(() => {
        setLoading(false);
        setInitialLoad(false);
        fetchData();
      }, 2000);
    } else {
      if (selectedYear) {
        debouncedFetchData(selectedYear, "");
      } else if (selectedDepartment) {
        debouncedFetchData("", selectedDepartment);
      } else if (searchTerm) {
        debouncedFetchData("", "", searchTerm);
      } else {
        debouncedFetchData();
      }
    }
  }, [selectedYear, selectedDepartment, searchTerm, initialLoad]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    setSelectedDepartment("");
    setSearchTerm("");
  };

  const handleDepartmentChange = (e) => {
    const department = e.target.value;
    setSelectedDepartment(department);
    setSelectedYear("");
    setSearchTerm("");
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setSelectedYear("");
    setSelectedDepartment("");
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2000 }, (_, i) => 2001 + i).reverse();

  const departmentOptions = ["SEBI", "RBI", "CBDT"];

  return (
    <>
      {initialLoad && loading ? (
        <Loader />
      ) : (
        <div className="regulatory-updates-section container">
          <h2>Regulatory updates</h2>
          <div className="updates-box">
            <h4 className="list-head">List of All Regulations</h4>
            <p className="list-para">
              Regulatory Reforms Initiated Since 2016 To Enhance Ease Of Doing
              Business, Ease Of Raising Capital And Reduce Compliance Burden For
              The Startup Ecosystem.
            </p>
            <div className="updates-table">
              <div className="update-table-header">
                <div className="filter-dropdown">
                  <span className="date-filter">
                    <label htmlFor="date">Filter by Year of Amendment</label>
                    <select name="date" id="date" value={selectedYear} onChange={handleYearChange}>
                      <option value="">All years</option>
                      {yearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </span>
                  <span className="dept-filter">
                    <label htmlFor="department">Filter by Ministry/Department</label>
                    <select name="department" id="department" value={selectedDepartment} onChange={handleDepartmentChange}>
                      <option value="">All departments</option>
                      {departmentOptions.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </span>
                </div>
                <div className="table-search">
                  <input type="text" placeholder="Search regulatory" value={searchTerm} onChange={handleSearchChange} />
                  <i className="fa fa-search"></i>
                </div>
              </div>
              <div className="ag-theme-alpine">
                {error ? (
                  <p>{error}</p>
                ) : rowData.length === 0 ? (
                  <p>No data available for the selected filters.</p>
                ) : (
                  <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    pagination={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RegulatoryUpdates;